import React, { useCallback, useEffect, useState } from 'react';
import { fetchCko2026WarmupQueueStatus } from '../cko2026Warmup';
import type { Cko2026WarmupQueueStatus } from '../types';

const CKO2026WarmupQueuePanel: React.FC = () => {
  const [status, setStatus] = useState<Cko2026WarmupQueueStatus | null>(null);
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const s = await fetchCko2026WarmupQueueStatus();
      setStatus(s);
    } finally {
      setLoading(false);
    }
  }, []);

  // 固定节奏自动刷新。
  // 之前是用 status 作为开关：只要某一次请求失败（status 被置回 null），
  // 轮询条件就再也不成立，面板会一直停在「无法获取队列状态」直到重新挂载。
  useEffect(() => {
    let cancelled = false;
    let timer: number | null = null;

    const tick = async () => {
      if (cancelled) return;
      await refresh();
      if (cancelled) return;
      timer = window.setTimeout(tick, 3000);
    };

    void tick();
    return () => {
      cancelled = true;
      if (timer !== null) window.clearTimeout(timer);
    };
  }, [refresh]);

  const phaseLabel = (phase: string) => {
    switch (phase) {
      case 'queued': return '排队中...';
      case 'claimed': return '已领单...';
      case 'downloading': return '下载源视频...';
      case 'probing': return '探测媒体...';
      case 'copying': return '快速封装中...';
      case 'encoding': return '转码中...';
      case 'uploading': return '上传成品...';
      case 'done': return '处理完成';
      case 'failed': return '处理失败';
      case 'cancelled': return '任务取消';
      default: return '处理中...';
    }
  };

  const workerStateLabel = (queueStatus: Cko2026WarmupQueueStatus) => {
    switch (queueStatus.workerState) {
      case 'active':
        return '活跃中';
      case 'idle':
        return queueStatus.mode === 'remote' ? '空闲中' : '未运行';
      case 'idle_unknown':
        return queueStatus.mode === 'remote' ? '空闲/未知' : '未运行';
      default:
        return queueStatus.workerRunning ? '活跃中' : '未运行';
    }
  };

  const taskStatusLabel = (value?: string) => {
    switch (value) {
      case 'queued': return '排队';
      case 'claimed': return '已领单';
      case 'processing': return '处理中';
      case 'done': return '完成';
      case 'failed': return '失败';
      case 'cancelled': return '取消';
      default: return value || '-';
    }
  };

  const formatTime = (value?: number | string) => {
    if (typeof value === 'number') {
      if (!value) return '-';
      return new Date(value).toLocaleString('zh-CN');
    }
    if (typeof value === 'string' && value.trim() !== '') {
      const normalized = value.includes('T') ? value : value.replace(' ', 'T');
      const date = new Date(normalized);
      if (!Number.isNaN(date.getTime())) {
        return date.toLocaleString('zh-CN');
      }
      return value;
    }
    return '-';
  };

  const isRemote = status?.mode === 'remote';

  return (
    <div className="w-full bg-gray-900/60 backdrop-blur border border-gray-700/50 rounded-lg overflow-hidden">
      <button
        onClick={() => { setExpanded(!expanded); if (!expanded) refresh(); }}
        className="w-full px-4 py-3 flex items-center justify-between text-sm text-gray-300 hover:bg-gray-800/40 transition-colors"
      >
        <div className="flex items-center gap-2">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
          </svg>
          <span>转码队列</span>
          {status && (
            <span className={`ml-1 px-1.5 py-0.5 text-xs rounded-full ${status.taskCount > 0 ? 'bg-amber-500/20 text-amber-300' : 'bg-green-500/20 text-green-300'}`}>
              {status.taskCount} 待处理
            </span>
          )}
          {status && (
            <span className={`ml-1 px-1.5 py-0.5 text-xs rounded-full ${status.workerRunning ? 'bg-cyan-500/20 text-cyan-300' : 'bg-red-500/20 text-red-300'}`}>
              {isRemote ? 'ECS Worker' : '本地 Worker'} {workerStateLabel(status)}
            </span>
          )}
          {!status && <span className="text-gray-500 text-xs">加载中...</span>}
        </div>
        <svg
          className={`w-4 h-4 transition-transform ${expanded ? 'rotate-180' : ''}`}
          fill="none" stroke="currentColor" viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {expanded && (
        <div className="px-4 pb-4 border-t border-gray-700/50">
          <div className="flex items-center gap-2 mt-3 mb-2">
            <button
              onClick={refresh}
              disabled={loading}
              className="px-3 py-1 text-xs bg-cyan-500/20 text-cyan-300 rounded hover:bg-cyan-500/30 disabled:opacity-50 transition-colors"
            >
              {loading ? '刷新中...' : '刷新'}
            </button>
          </div>

          {status && (
            <div className="space-y-3 text-xs text-gray-400">
              {status.summary && (
                <div>
                  <div className="text-gray-500 mb-1">{isRemote ? '远程任务摘要：' : '队列摘要：'}</div>
                  <div className="grid grid-cols-3 gap-2 sm:grid-cols-6">
                    <div className="rounded bg-gray-800/50 px-2 py-1">
                      <div className="text-[10px] text-gray-500">排队</div>
                      <div className="text-amber-300">{status.summary.queued}</div>
                    </div>
                    <div className="rounded bg-gray-800/50 px-2 py-1">
                      <div className="text-[10px] text-gray-500">已领单</div>
                      <div className="text-cyan-300">{status.summary.claimed}</div>
                    </div>
                    <div className="rounded bg-gray-800/50 px-2 py-1">
                      <div className="text-[10px] text-gray-500">处理中</div>
                      <div className="text-cyan-300">{status.summary.processing}</div>
                    </div>
                    <div className="rounded bg-gray-800/50 px-2 py-1">
                      <div className="text-[10px] text-gray-500">完成</div>
                      <div className="text-green-300">{status.summary.done}</div>
                    </div>
                    <div className="rounded bg-gray-800/50 px-2 py-1">
                      <div className="text-[10px] text-gray-500">失败</div>
                      <div className="text-red-300">{status.summary.failed}</div>
                    </div>
                    <div className="rounded bg-gray-800/50 px-2 py-1">
                      <div className="text-[10px] text-gray-500">取消</div>
                      <div className="text-zinc-300">{status.summary.cancelled}</div>
                    </div>
                  </div>
                </div>
              )}

              {/* 任务列表 */}
              {status.tasks.length > 0 ? (
                <div>
                  <div className="text-gray-500 mb-1">{isRemote ? '远程排队任务：' : '待处理任务：'}</div>
                  <div className="space-y-1 max-h-48 overflow-y-auto">
                    {status.tasks.map((task) => (
                      <div
                        key={task.taskId}
                        className="flex items-center justify-between gap-2 px-2 py-1 bg-gray-800/50 rounded"
                      >
                        <div className="min-w-0">
                          <div className="text-gray-300 truncate">{task.trickId} {task.trickName}</div>
                          <div className="text-[10px] text-gray-500">
                            用户 #{task.wpUserId} · {taskStatusLabel(task.status)}
                            {task.workerHost ? ` · ${task.workerHost}` : ''}
                          </div>
                        </div>
                        <span className="shrink-0 text-gray-500 text-[10px]">
                          {formatTime(task.createdAt || task.uploadedAt)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="text-green-400">{isRemote ? '远程队列为空，无待处理任务。' : '队列为空，无待处理任务。'}</div>
              )}

              {/* 当前正在处理的转码任务 */}
              {status.currentTask && (
                <div className="border-t border-cyan-400/20 bg-cyan-500/5 rounded-lg p-3">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-cyan-400 animate-pulse" />
                      <span className="text-cyan-300 text-xs font-medium">正在处理</span>
                    </div>
                    <span className="text-gray-500 text-[10px]">
                      {phaseLabel(status.currentTask.phase)}
                    </span>
                  </div>
                  <div className="text-white text-sm mb-1">
                    {status.currentTask.trickName}
                  </div>
                  <div className="text-gray-500 text-[10px] mb-2">
                    招式 {status.currentTask.trickId} · 用户 #{status.currentTask.wpUserId}
                    {status.currentTask.workerHost ? ` · Worker ${status.currentTask.workerHost}` : ''}
                  </div>
                  {/* Progress bar */}
                  <div className="w-full bg-gray-700/50 rounded-full h-2.5 overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all duration-300 ease-out"
                      style={{
                        width: `${status.currentTask.progress}%`,
                        background: status.currentTask.phase === 'probing'
                          ? 'linear-gradient(90deg, #6366f1, #818cf8)'
                          : 'linear-gradient(90deg, #06b6d4, #22d3ee)',
                      }}
                    />
                  </div>
                  <div className="flex justify-between mt-1">
                    <span className="text-gray-500 text-[10px]">
                      {status.currentTask.progress}%
                    </span>
                    {status.currentTask.durationMs > 0 ? (
                      <span className="text-gray-600 text-[10px]">
                        视频时长 {(status.currentTask.durationMs / 1000).toFixed(1)}s
                      </span>
                    ) : (
                      <span className="text-gray-600 text-[10px]">
                        {status.currentTask.claimedAt ? `开始于 ${formatTime(status.currentTask.claimedAt)}` : ''}
                      </span>
                    )}
                  </div>
                </div>
              )}

              {/* 服务器信息 */}
              <div className="border-t border-gray-700/30 pt-2 space-y-1">
                <div className="text-gray-500 mb-1">{isRemote ? '远程模式信息：' : '服务器信息：'}</div>
                {isRemote ? (
                  <div className="grid grid-cols-2 gap-x-2 gap-y-0.5">
                    <span className="text-gray-500">模式：</span>
                    <span className="text-cyan-300">远程 ECS Worker</span>
                    <span className="text-gray-500">任务表：</span>
                    <span className="text-green-400 break-all">{status.serverInfo.taskTable || '未知'}</span>
                    <span className="text-gray-500">当前 Worker：</span>
                    <span className={status.serverInfo.currentWorkerHost ? 'text-green-400' : 'text-zinc-400'}>
                      {status.serverInfo.currentWorkerHost || '空闲/未知'}
                    </span>
                    <span className="text-gray-500">活跃主机：</span>
                    <span className="text-green-400 break-all">
                      {status.serverInfo.activeWorkerHosts && status.serverInfo.activeWorkerHosts.length > 0
                        ? status.serverInfo.activeWorkerHosts.join(', ')
                        : '无'}
                    </span>
                    <span className="text-gray-500">远程开关：</span>
                    <span className={status.serverInfo.remoteWorkerEnabled ? 'text-green-400' : 'text-red-400'}>
                      {status.serverInfo.remoteWorkerEnabled ? '已开启' : '未开启'}
                    </span>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-x-2 gap-y-0.5">
                    <span className="text-gray-500">exec()：</span>
                    <span className={status.serverInfo.execAvailable ? 'text-green-400' : 'text-red-400'}>
                      {status.serverInfo.execAvailable ? '可用' : '已禁用'}
                    </span>
                    <span className="text-gray-500">ffmpeg：</span>
                    <span className={status.serverInfo.ffmpegPath !== 'NOT FOUND' ? 'text-green-400' : 'text-red-400'}>
                      {status.serverInfo.ffmpegPath !== 'NOT FOUND' ? status.serverInfo.ffmpegPath : '未找到'}
                    </span>
                    <span className="text-gray-500">ffprobe：</span>
                    <span className={status.serverInfo.ffprobePath !== 'NOT FOUND' ? 'text-green-400' : 'text-red-400'}>
                      {status.serverInfo.ffprobePath !== 'NOT FOUND' ? status.serverInfo.ffprobePath : '未找到'}
                    </span>
                    <span className="text-gray-500">PHP CLI：</span>
                    <span className={status.serverInfo.phpCliPath !== 'NOT FOUND' ? 'text-green-400' : 'text-red-400'}>
                      {status.serverInfo.phpCliPath !== 'NOT FOUND' ? status.serverInfo.phpCliPath : '未找到'}
                    </span>
                    <span className="text-gray-500">Worker 脚本：</span>
                    <span className={status.serverInfo.workerScriptExists ? 'text-green-400' : 'text-red-400'}>
                      {status.serverInfo.workerScriptExists ? '已部署' : '缺失'}
                    </span>
                    <span className="text-gray-500">Worker：</span>
                    <span className={status.workerRunning ? 'text-cyan-400' : 'text-red-400'}>
                      {workerStateLabel(status)}
                    </span>
                  </div>
                )}
              </div>

              {/* 诊断建议 */}
              {!isRemote && status.taskCount > 0 && !status.workerRunning && (
                <div className="bg-red-500/10 border border-red-500/30 rounded p-2 text-red-300">
                  <div className="font-medium mb-1">Worker 未运行，{status.taskCount} 个任务等待处理。</div>
                  {!status.serverInfo.execAvailable && (
                    <div>需要启用 PHP exec() 函数。</div>
                  )}
                  {!status.serverInfo.workerScriptExists && (
                    <div>需要部署 cko2026_warmup_worker.php 到服务器。</div>
                  )}
                  {status.serverInfo.execAvailable && status.serverInfo.workerScriptExists && status.serverInfo.phpCliPath !== 'NOT FOUND' && (
                    <div>
                      请在服务器执行：
                      <code className="block mt-1 px-2 py-1 bg-red-500/20 rounded text-[11px] break-all">
                        {status.serverInfo.phpCliPath} {status.serverInfo.taskDir.replace('.queue/tasks', 'cko2026_warmup_worker.php')}
                      </code>
                    </div>
                  )}
                </div>
              )}
              {isRemote && status.taskCount > 0 && !status.workerRunning && (
                <div className="bg-amber-500/10 border border-amber-500/30 rounded p-2 text-amber-200">
                  <div className="font-medium mb-1">远程队列中仍有 {status.taskCount} 个任务等待领单。</div>
                  <div>如果 ECS 常驻 Worker 正在运行但这里长期不变，优先检查 ECS 上的 `cko2026_warmup_remote_worker.php` 进程与网站机任务表写入。</div>
                </div>
              )}
            </div>
          )}

          {!status && !loading && (
            <div className="text-red-400 text-xs">无法获取队列状态，请确保已部署最新 API 文件。</div>
          )}
        </div>
      )}
    </div>
  );
};

export default CKO2026WarmupQueuePanel;
